import React, { FC, useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clamp, sortBy } from 'lodash'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import VideoGuide from '../../../components/common/VideoGuide'
import { compile } from '../../../../utils/search'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import { IconName } from '@fortawesome/fontawesome-common-types'
import AddMemberModule from '../../../components/setting/team/AddMemberModule'
import Modal from '../../../components/common/Modal'
import {
  createSettingTeam,
  delSettingTeam,
  fetchSettingTeam,
  fetchSettingTeamDetail,
  updateSettingTeam,
  updateSettingTeamManager,
} from '../../../../states/containers/setting/systemSlice'
import {
  RequestPatchSettingTeam,
  RequestPatchSettingTeamManager,
  ResponseGetSettingTeamDetail,
  RoleMember,
  SettingTeam,
  TeamPermission,
} from '../../../../states/types/setting'
import { replaceAndStrip } from '../../../../utils/string'
import { useSnackbar } from '../../../components/common/snackbar/SnackbarProvider'
import {
  DndContext,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { updateSettingTeamCreateTeam, updateSettingTeamOpenTeamIds } from '../../../../states/containers/common/currentSlice'

interface Team extends SettingTeam {
  button?: boolean
  level?: number
  parent_list?: {id: number, parent_id: number, name: string, order_index: number}[]
}

interface TeamDetail extends ResponseGetSettingTeamDetail {
  isCommon?: boolean
}

interface permissionDetail {
  member_id: number
  use_team_setting: boolean
  permission: TeamPermission
}

type TeamPermissionKeys = keyof TeamPermission

const VIEW_OTHER_PERMISSION_LIST = [
  {
    type: "risk_team_report",
    icon: "heartbeat",
    label: "リスクアセスメント チームレポート",
    label_sub: "(配下メンバーのみ)",
    label_short: "チーム",
    size: "100px",
  },
  {
    type: "risk_member_report",
    icon: "heartbeat",
    label: "リスクアセスメント ユーザーレポート",
    label_sub: "(配下メンバーのみ)",
    label_short: "ユーザー",
    size: "100px",
  },
]

const VIEW_TASK_PERMISSION_LIST = [
  {
    type: "task_labeling_board",
    icon: "tag",
    label: "メンバー業務仕分けボード",
    label_sub: "(配下メンバーのみ)",
    label_short: "業務仕分け",
    size: "100px",
  },
  {
    type: "task_labeling_status",
    icon: "clipboard-list",
    label: "メンバー仕分け状況レポート",
    label_sub: "(配下メンバーのみ)",
    label_short: "仕分け状況",
    size: "100px",
  },
  {
    type: "task_member_cost_view",
    icon: "wallet",
    label: "ユーザーコスト表示",
    label_sub: "(配下メンバーのみ)",
    label_short: "ユーザーコスト",
    size: "100px",
  },
]

const VIEW_PERMISSION_LIST = [...VIEW_OTHER_PERMISSION_LIST, ...VIEW_TASK_PERMISSION_LIST]

const DETAIL_TAB_TYPE = [
  {
    type: "current",
    icon: "heart",
    title: "このチームに対する閲覧権限",
  },
  {
    type: "other",
    icon: "sitemap",
    title: "配下チームに対する閲覧権限",
  },
]

const INDENTATION_WIDTH = 34

const Index: FC = () => {
  const dispatch = useDispatch()
  const { language } = useParams<{ language: LangValue }>()
  const { enqueue, closeQueue } = useSnackbar()
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const {
    login,
    current,
  } = useSelector((state: RootState) => state)

  const [teamList, setTeamList] = useState<SettingTeam[]>([])
  const [memberList, setMemberList] = useState<RoleMember[]>([])
  const [unassignedMembers, setUnassignedMembers] = useState<RoleMember[]>([])
  const [isUpdate, setIsUpdate] = useState<boolean>(false)

  const teamPermissionInit = {
    risk_team_report: true,
    risk_member_report: true,
    task_labeling_board: true,
    task_labeling_status: true,
    task_member_cost_view: false,
  }
  const [teamPermission, setTeamPermission] = useState<TeamPermission>(teamPermissionInit)

  const selectTeamInit = {
    id: 0,
    name: "",
    code: null,
    managers: [],
    self: {
      permission: teamPermissionInit,
      individual_settings: [],
      members: [],
    },
    nested: {
      permission: teamPermissionInit,
      individual_settings: [],
      teams: [],
    },
  }

  const [selectTeam, setSelectTeam] = useState<TeamDetail>(selectTeamInit)

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [isDisplayDetail, setIsDisplayDetail] = useState<boolean>(false)
  const [selectTabType, setSelectTabType] = useState<string>("current")
  const [isDisplayMiniMenu, setIsDisplayMiniMenu] = useState<boolean>(false)
  const [managerDetail, setManagerDetail] = useState<permissionDetail>()
  const [isSortMode, setIsSortMode] = useState<boolean>(false)
  const [isCreateMode, setIsCreateMode] = useState<boolean>(true)

  const [inputName, setInputName] = useState<string>("")
  const [nameComposition, setNameComposition] = useState<boolean>(false)
  const [nameError, setNameError] = useState<string>("")

  const [inputCode, setInputCode] = useState<string | null>("")
  const [codeComposition, setCodeComposition] = useState<boolean>(false)

  const [selectTeamId, setSelectTeamId] = useState<number>()
  const [selectManager, setSelectManager] = useState<RoleMember[]>([])
  const [selectMember, setSelectMember] = useState<RoleMember[]>([])

  const [selectManagerDetailCommonType, setSelectManagerDetailCommonType] = useState<boolean>(false)
  const [selectManagerDetailTeamPermission, setSelectManagerDetailTeamPermission] = useState<TeamPermission>(teamPermissionInit)

  const [inputNewName, setInputNewName] = useState<string>("")
  const [newNameError, setNewNameError] = useState<string>("")

  const [deleteTeamData, setDeleteTeamData] = useState<{ id: number, name: string }>()
  const [selectParentTeamData, setSelectParentTeamData] = useState<{ id: number, parent_id?: number, name: string }>()

  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false)

  const [searchWord, setSearchWord] = useState<string>("")
  const [teams, setTeams] = useState<SettingTeam[]>([])

  const [dragItem, setDragItem] = useState<{id: number, name: string, level: number, order_index: number}>()
  const [moveTarget, setMoveTarget] = useState<{id: number, parent_id: number, name: string, level: number, order_index: number}>()
  const [isSortSubmit, setIsSortSubmit] = useState<boolean>(false)
  const [isGripping, setIsGripping] = useState<boolean>(false)

  const [createTeamData, setCreateTeamData] = useState<{ parentId: number | null, name?: string }>()
  const [openTeamIds, setOpenTeamIds] = useState<number[]>([])

  const permissionData = useMemo(() => {
    if (selectTabType === "other") {
      setTeamPermission(selectTeam.nested.permission)
      return ({
        permission: selectTeam.nested.permission,
        individual_settings: selectTeam.nested.individual_settings,
      })
    }
    setTeamPermission(selectTeam.self.permission)
    return ({
      permission: selectTeam.self.permission,
      individual_settings: selectTeam.self.individual_settings,
    })
  }, [selectTabType, selectTeam])

  const getChildrenTeamCount = (id: number) => (
    teamList.filter((v) => v.parent_team_id === id).length
  )

  const openableTeamIds = useMemo(() => (
    teamList.filter((v) => !!getChildrenTeamCount(v.id)).map((v) => v.id)
  ), [teamList])

  const getParentTeamIds = (id: number | null): number[] => {
    if (id === null) return []
    const targetTeam = teamList.find((v) => v.id === id)
    if (!targetTeam || !targetTeam.parent_team_id) return []
    return [targetTeam.parent_team_id, ...getParentTeamIds(targetTeam.parent_team_id)]
  }

  const getParentTeamNameList = (id: number | null): string[] => {
    if (id === null) return []
    const targetTeam = teamList.find((v) => v.id === id)
    if (!targetTeam || !targetTeam.parent_team_id) return []
    return [targetTeam.name, ...getParentTeamNameList(targetTeam.parent_team_id)]
  }

  const buildFullPathName = (id: number) => getParentTeamNameList(id).reverse().join(" > ")

  const displayUpdateToast = (queueId: number) => {
    setTimeout(() => {
      setIsUpdate(false)
      closeQueue(queueId)
      enqueue({
        type: "success",
        message: "設定を更新しました",
      })
    }, 1500)
  }

  const changeInputName = (name: string) => {
    setInputName(name)
    const formatName = replaceAndStrip(name)
    if (teamList.filter((v) => v.id !== selectTeam?.id && v.name === formatName).length) {
      setNameError("同名のチームが存在します。異なるチーム名を設定してください。")
      setIsUpdate(false)
      return
    }
    setNameError("")
    setIsUpdate(true)
  }

  const changeInputCode = (code: string) => {
    setInputCode(code)
    setIsUpdate(true)
  }

  const refreshUnassignedMembers = () => {
    dispatch(
      fetchSettingTeam((result) => {
        setUnassignedMembers(result.unassigned_members)
      }),
    )
  }

  const changeManager = (memberIds: number[]) => {
    setSelectManager(memberList.filter((v) => memberIds.includes(v.id)))
    const tmpValues = memberIds.map((v) => ({
      member_id: v,
      use_team_setting: true,
      permission: {
        risk_member_report: true,
        risk_team_report: true,
        task_labeling_board: true,
        task_labeling_status: true,
        task_member_cost_view: false,
      },
    }))
    setSelectTeam((cur) => ({
      ...cur,
      nested: { ...cur.nested, individual_settings: [...cur.nested.individual_settings, ...tmpValues] },
      self: { ...cur.self, individual_settings: [...cur.nested.individual_settings, ...tmpValues] },
    }))
    setIsUpdate(true)
  }

  const ejectManagerId = (memberId: number) => {
    setSelectManager((cur) => cur.filter((v) => v.id !== memberId))
    setSelectTeam((cur) => ({
      ...cur,
      nested: { ...cur.nested, individual_settings: cur.nested.individual_settings.filter((v) => v.member_id !== memberId) },
      self: { ...cur.self, individual_settings: cur.nested.individual_settings.filter((v) => v.member_id !== memberId) },
    }))
    setIsUpdate(true)
  }

  const changeMember = (memberIds: number[]) => {
    setSelectMember(memberList.filter((v) => memberIds.includes(v.id)))
    setTeamList((cur) => cur.map((v) => (v.id === selectTeamId ? { ...v, member_count: memberIds.length } : v)))
    setIsUpdate(true)
  }

  const ejectMemberId = (memberId: number) => {
    setSelectMember((cur) => cur.filter((v) => v.id !== memberId))
    setTeamList((cur) => cur.map((v) => (v.id === selectTeamId ? { ...v, member_count: v.member_count - 1 } : v)))
    setIsUpdate(true)
  }

  const changePermission = (type: string) => {
    setTeamPermission((cur) => ({ ...cur, [type]: !cur[type as TeamPermissionKeys] }))
    setIsUpdate(true)
  }

  const changeInputNewName = (name: string) => {
    setInputNewName(name)
    const formatName = replaceAndStrip(name)
    if (teamList.filter((v) => v.name === formatName).length) {
      setNewNameError("同名のチームが存在します。異なるチーム名を設定してください。")
      return
    }
    setNewNameError("")
  }

  const reset = () => {
    setSearchWord("")
  }

  const searchTeam = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setTeams([...teamList])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = teamList.filter((team) => {
      if (team === null) return false
      return compile(word).test(team.name.toLowerCase())
    })
    setTeams(sortBy(list, 'id'))
  }

  const clickTeam = (id: number) => {
    setSelectTeamId(id)
    setIsDisplayDetail(true)
    setSelectTabType("current")
    setIsDetailLoading(true)
    setNameError("")
    const tmpOpenTeamIds = Array.from(new Set([...openTeamIds, ...getParentTeamIds(id)]))
    setOpenTeamIds(tmpOpenTeamIds)
    dispatch(updateSettingTeamOpenTeamIds(tmpOpenTeamIds))
    dispatch(
      fetchSettingTeamDetail(
        { id: id.toString() },
        (resultDetail) => {
          setSelectTeam({ ...resultDetail, isCommon: !teamList.find((v) => v.id === id)?.parent_team_id })
          setSelectManager(resultDetail.managers)
          setSelectMember(resultDetail.self.members)
          setInputName(resultDetail.name)
          setInputCode(resultDetail.code)
          setIsDetailLoading(false)
        },
      ),
    )
  }

  const closeDetail = () => {
    setIsDisplayDetail(false)
    setSelectTeam(selectTeamInit)
    setSelectTeamId(undefined)
  }

  const toggleDetailPermission = (type: string) => {
    setSelectManagerDetailTeamPermission((cur) => ({ ...cur, [type]: !cur[type as TeamPermissionKeys] }))
  }

  const createTeam = () => {
    setIsDetailLoading(true)
    setSelectTabType("current")
    dispatch(
      createSettingTeam(
        {
          parent_team_id: createTeamData?.parentId || 0,
          name: inputNewName || "",
        },
        (resultNew) => {
          setSelectTeamId(resultNew.id)
          setIsDisplayDetail(true)
          dispatch(
            fetchSettingTeam((result) => {
              setIsLoading(false)
              setTeamList(result.teams)
              setMemberList(result.members)
              setUnassignedMembers(result.unassigned_members)
              dispatch(
                fetchSettingTeamDetail(
                  { id: resultNew.id.toString() },
                  (resultDetail) => {
                    setSelectTeam({ ...resultDetail, isCommon: !result.teams.find((v) => v.id === resultNew.id)?.parent_team_id })
                    setSelectManager(resultDetail.managers)
                    setSelectMember(resultDetail.self.members)
                    setInputName(resultDetail.name)
                    setInputCode(resultDetail.code)
                    setIsDetailLoading(false)
                    setCreateTeamData(undefined)
                  },
                ),
              )
            }),
          )
        },
      ),
    )
  }

  const deleteTeam = () => {
    if (!deleteTeamData) return
    dispatch(
      delSettingTeam(
        { id: deleteTeamData.id.toString() },
        () => {
          enqueue({
            type: "success",
            message: "チームを削除しました",
          })
          setDeleteTeamData(undefined)
          setIsDisplayMiniMenu(false)
          setIsDisplayDetail(false)
          setSelectTeamId(undefined)
          dispatch(
            fetchSettingTeam((result) => {
              setIsLoading(false)
              setTeamList(result.teams)
              setMemberList(result.members)
              setUnassignedMembers(result.unassigned_members)
            }),
          )
        },
      ),
    )
  }

  const selectParentTeam = () => {
    if (!selectParentTeamData) return
    const queueId = enqueue({
      type: "progress",
      message: "設定を更新しています…",
    })
    const tmpParam: RequestPatchSettingTeam = {
      id: selectParentTeamData?.id.toString() || "0",
      parent_team_id: selectParentTeamData?.parent_id,
    }
    dispatch(
      updateSettingTeam(
        tmpParam,
        () => {
          displayUpdateToast(queueId)
          setDragItem(undefined)
          setIsDisplayMiniMenu(false)
          setSelectParentTeamData(undefined)
          dispatch(
            fetchSettingTeam((result) => {
              setIsLoading(false)
              setTeamList(result.teams)
              setMemberList(result.members)
              setUnassignedMembers(result.unassigned_members)
              setIsSortSubmit(false)
            }),
          )
        },
      ),
    )
  }

  const memberChip = (member: RoleMember, deleteCallback?: (id: number) => void) => (
    <div
      style={{
        position: "relative",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "left",
        gap: "4px",
        userSelect: "none",
        border: "solid 1px #CCCCCC",
        borderRadius: "20px",
      }}
    >
      <img
        alt={member.name}
        src={member.profile_image ?? `/static/images/profile/${(member.id || 0) % 12}.jpeg`}
        style={{ width: "16px", height: "16px", borderRadius: "50%" }}
      />
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        {member.name}
      </div>
      {
        member.active || (
          <Tips
            description="休眠中💤"
            className=""
            icon={false}
          >
            <>
              💤
            </>
          </Tips>
        )
      }
      {
        deleteCallback && (
          <span
            style={{
              marginLeft: "4px",
              cursor: "pointer",
            }}
            onClick={() => deleteCallback(member.id)}
            role="none"
          >
            <FontAwesomeIcon icon={['fas', 'times']} />
          </span>
        )
      }
    </div>
  )

  const createNewTeam = (data: { parentId: number }) => {
    setCreateTeamData(data)
    setInputNewName("")
  }

  const SortableRow = ({ id, row, children }: {id: string, row: Team, children: React.ReactElement}) => {
    const { setNodeRef, transform, transition, isDragging } = useSortable({
      id,
      data: { row },
    })

    return (
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          position: "relative",
        }}
        draggable={false}
      >
        {
          !!moveTarget?.parent_id && isDragging && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: `${(moveTarget?.level || 0) * 36}px`,
                transform: "translateY(-50%)",
                width: "2px",
                height: "30px",
                backgroundColor: "#FF0000",
              }}
            />
          )
        }
        <div
          style={{
            opacity: isDragging ? 0 : 1,
          }}
        >
          {children}
        </div>
      </div>
    )
  }

  const SortableArea = ({ id, row, children }: {id: string, row: Team, children: React.ReactElement}) => {
    const { attributes, listeners } = useSortable({
      id,
      data: { row },
    })

    return (
      <div
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
        }}
      >
        {children}
      </div>
    )
  }

  const getParentTeamList = (id: number | null): {id: number, parent_id: number, name: string, order_index: number}[] => {
    if (id === null) return []
    const targetTeam = teamList.find((v) => v.id === id)
    if (!targetTeam) return []
    return [
      { id: targetTeam.id, parent_id: targetTeam.parent_team_id || 0, name: targetTeam.name, order_index: targetTeam.order_index },
      ...getParentTeamList(targetTeam.parent_team_id),
    ]
  }

  const buildParentList = (id: number) => getParentTeamList(id).reverse()

  const buildFlatTeams = (level = 0, parent_id?: number | null): Team[] => {
    let list: Team[] = []
    const levelCount = level + 1
    if (parent_id === 0) return ([])
    if (parent_id) {
      list = teamList.filter((v) => v.parent_team_id === parent_id)
        .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: level, parent_list: buildParentList(v.id) }))
    } else {
      list = teamList.filter((v) => v.parent_team_id === null)
        .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: level, parent_list: buildParentList(v.id) }))
    }
    if (!list.length) return ([])
    const result: Team[] = []
    list.forEach((v) => {
      result.push(v)
      result.push(...buildFlatTeams(levelCount, v.id).flat())
    })
    return result
  }

  const flatTeams = useMemo(() => buildFlatTeams(), [teamList])

  const toggleOpenTeamIds = (id: number) => {
    if (openTeamIds.includes(id)) {
      const tmpOpenTeamIds = openTeamIds.filter((v) => v !== id)
      setOpenTeamIds(tmpOpenTeamIds)
      dispatch(updateSettingTeamOpenTeamIds(tmpOpenTeamIds))
    } else {
      const tmpOpenTeamIds = [...openTeamIds, id]
      setOpenTeamIds(tmpOpenTeamIds)
      dispatch(updateSettingTeamOpenTeamIds(tmpOpenTeamIds))
    }
  }

  const buildTeamList = (data: {level: number, parent_id?: number | null, rules?: boolean[], isCreate?: boolean}):JSX.Element => {
    let list: Team[] = []
    const levelCount = data.level + 1
    if (data.parent_id === 0) return (<></>)
    if (data.isCreate) {
      if (data.parent_id) {
        list = teamList.filter((v) => v.parent_team_id === data.parent_id)
          .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: data.level, parent_list: buildParentList(v.id) }))
      } else {
        list = teamList.filter((v) => v.parent_team_id === null)
          .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: data.level, parent_list: buildParentList(v.id) }))
      }
    }
    if (isCreateMode) {
      list.push({
        id: 0,
        name: "",
        code: null,
        parent_team_id: data.parent_id ?? null,
        member_count: 0,
        under_member_count: 0,
        order_index: list.length,
        button: true,
      })
    }
    return (
      <>
        {
          list.map((v, i) => {
            if (!data.level && !v.id) return (<></>)
            const childrenCount = getChildrenTeamCount(v.id)
            const ruleList = [...data.rules?.map((rule, index) => (data.rules?.length === index + 1 ? !rule : rule)) || [], list.length === i + 1]
            const dom = (
              <div
                style={{
                  position: "relative",
                }}
              >
                <div
                  className={`${!!v.id && "hover"}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: "40px",
                    padding: "4px",
                    width: "100%",
                    backgroundColor: selectTeamId === v.id ? "#ffeded" : "",
                    borderBottom: "none",
                  }}
                  onClick={() => v.id && clickTeam(v.id)}
                  role="none"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {
                      ruleList.map((w, j) => {
                        if (!j) return (<React.Fragment key={`${v.id}_${j + 0}_${data.level}`} />)
                        return (
                          <div
                            key={`${v.name}_${j + 0}_${data.level}`}
                            style={{
                              width: "14px",
                              height: "100%",
                              position: "relative",
                              border: "none",
                            }}
                          >
                            {
                              ruleList.length !== j + 1 && (
                                <>
                                  {w && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: "-20px",
                                        left: "7px",
                                        width: "1px",
                                        height: "64px",
                                        backgroundColor: "#999999",
                                      }}
                                    />
                                  )}
                                </>
                              )
                            }
                          </div>
                        )
                      })
                    }
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        position: "relative",
                      }}
                    >
                      {
                        !!data.level && v.button ? (
                          <>
                            <button
                              type="button"
                              style={{
                                border: "solid 1px #42B07B",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                backgroundColor: "#E7FFE8",
                                color: "#42B07B",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                              }}
                              onClick={() => createNewTeam({ parentId: v.parent_team_id || 0 })}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              チーム追加
                            </button>
                          </>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {
                              !v.button && !!childrenCount && (
                                <button
                                  type="button"
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    alignItems: "center",
                                    gap: "0px",
                                    padding: "4px",
                                    fontSize: "16px",
                                  }}
                                  onClick={(e) => {
                                    toggleOpenTeamIds(v.id)
                                    e.stopPropagation()
                                  }}
                                >
                                  <div>
                                    {
                                      openTeamIds.includes(v.id) ? (
                                        <FontAwesomeIcon icon={["fas", "angle-down"]} />
                                      ) : (
                                        <FontAwesomeIcon icon={["fas", "angle-right"]} />
                                      )
                                    }
                                  </div>
                                </button>
                              )
                            }
                            <div
                              style={{
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  fontSize: "7px",
                                  top: "-9px",
                                  whiteSpace: "nowrap",
                                  color: "#666666",
                                }}
                              >
                                {`第${levelCount}階層`}
                              </div>
                              {v.name}
                            </div>
                          </div>
                        )
                      }
                      {
                        !!data.level && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                left: "-4px",
                                width: "10px",
                                height: "1px",
                                backgroundColor: "#999999",
                                transform: "translateX(-100%)",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                transform: "translateY(-50%)",
                                left: "-15px",
                                width: "1px",
                                height: !i ? "30px" : "40px",
                                backgroundColor: "#999999",
                              }}
                            />
                          </>
                        )
                      }
                    </div>
                  </div>
                  {
                    !v.button && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {
                          !!childrenCount && !openTeamIds.includes(v.id) && (
                            <div
                              style={{
                                backgroundColor: "#FFFFFF",
                                color: "#666666",
                                border: "solid 1px #666666",
                                borderRadius: "14px",
                                padding: "4px 8px",
                              }}
                            >
                              {`配下チーム：${childrenCount}件`}
                            </div>
                          )
                        }
                        <div
                          style={{
                            backgroundColor: "#ECF2FD",
                            color: "#1D62E1",
                            borderRadius: "14px",
                            padding: "4px 8px",
                          }}
                        >
                          {`${v.member_count}名`}
                        </div>
                        <button
                          type="button"
                          onClick={() => clickTeam(v.id)}
                          style={{
                            backgroundColor: "#FFFFFF",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "4px 8px",
                            fontSize: "10px",
                          }}
                        >
                          選択
                        </button>
                      </div>
                    )
                  }
                </div>
              </div>
            )
            return (
              <React.Fragment key={`project_category_row_${v.id}`}>
                {dom}
                {buildTeamList({ level: levelCount, parent_id: v.id, rules: ruleList, isCreate: openTeamIds.includes(v.id) })}
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const buildSortTeamList = (level = 0, parent_id?: number | null, rules?: boolean[]):JSX.Element => {
    let list: Team[] = []
    const levelCount = level + 1
    if (parent_id === 0) return (<></>)
    if (parent_id) {
      list = teamList.filter((v) => v.parent_team_id === parent_id)
        .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: level, parent_list: buildParentList(v.id) }))
    } else {
      list = teamList.filter((v) => v.parent_team_id === null)
        .sort((a, b) => a.order_index - b.order_index).map((v) => ({ ...v, level: level, parent_list: buildParentList(v.id) }))
    }
    return (
      <>
        {
          list.map((v, i) => {
            const ruleList = [...rules?.map((rule, index) => (rules.length === index + 1 ? !rule : rule)) || [], list.length === i + 1]
            const dom = (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: "40px",
                  padding: "4px",
                  width: "100%",
                  backgroundColor: selectTeamId === v.id ? "#ffeded" : "",
                  borderBottom: "solid 1px #CCCCCC",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {
                    ruleList.map((w, j) => {
                      if (!j) return (<React.Fragment key={`${v.id}_${j + 0}_${level}`} />)
                      return (
                        <div
                          key={`${v.name}_${j + 0}_${level}`}
                          style={{
                            width: "30px",
                            height: "30px",
                            position: "relative",
                            backgroundColor: "#f7f7f7",
                            color: "#CCCCCC",
                            display: "flex",
                            flexFlow: "column",
                            gap: "0px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "20px",
                            }}
                          >
                            {j}
                          </div>
                          <div
                            style={{
                              fontSize: "7px",
                              marginTop: "-2px",
                            }}
                          >
                            階層
                          </div>
                        </div>
                      )
                    })
                  }
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      position: "relative",
                    }}
                  >
                    {
                      !!level && (
                        <SortableArea id={`project_category_row_${v.id}`} row={v}>
                          <div>
                            <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                          </div>
                        </SortableArea>
                      )
                    }
                    {v.name}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#ECF2FD",
                      color: "#1D62E1",
                      borderRadius: "14px",
                      padding: "4px 8px",
                    }}
                  >
                    {`${v.member_count}名`}
                  </div>
                  <button
                    type="button"
                    onClick={() => clickTeam(v.id)}
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      fontSize: "10px",
                    }}
                  >
                    選択
                  </button>
                </div>
              </div>
            )
            if (level) {
              return (
                <React.Fragment key={`project_category_row_${v.id}`}>
                  <SortableRow id={`project_category_row_${v.id}`} row={v}>
                    {dom}
                  </SortableRow>
                  {dragItem?.id !== v.id && buildSortTeamList(levelCount, v.id, ruleList)}
                </React.Fragment>
              )
            }
            return (
              <React.Fragment key={`project_category_row_${v.id}`}>
                {dom}
                {dragItem?.id !== v.id && buildSortTeamList(levelCount, v.id, ruleList)}
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    setIsGripping(true)
    const rowData = active.data.current?.row as Team
    setDragItem({ id: rowData.id, name: rowData.name, level: rowData.level || 0, order_index: rowData.order_index })
  }

  const handleDragEnd = () => {
    setIsGripping(false)
    if (!moveTarget?.parent_id) {
      setDragItem(undefined)
      return
    }
    setIsUpdate(true)
    setIsSortSubmit(true)
  }

  const handleDragMove = ({ delta, over }: DragMoveEvent) => {
    if (!over) return
    let rowData = over.data.current?.row as Team | undefined
    if (!rowData || !rowData.parent_list) return
    const currentIndex = flatTeams.findIndex((v) => v.id === rowData?.id)
    if (delta.y < 40) {
      if (currentIndex === 1) {
        rowData = { ...flatTeams[1 - 1] }
      } else {
        rowData = { ...flatTeams[currentIndex - 1] }
        rowData.parent_list = flatTeams[currentIndex - 1].parent_list?.map((v) => ({ ...v, order_index: v.order_index + 1 }))
      }
    } else {
      rowData.parent_list = flatTeams[currentIndex].parent_list?.map((v) => ({ ...v, order_index: v.order_index + 1 }))
    }
    if (!rowData || !rowData.parent_list) return
    const offset = Math.round(delta.x / INDENTATION_WIDTH)
    const currentLevel = clamp((dragItem?.level || 1) + offset, 0, rowData.parent_list.length - 1 || 1)
    let target = rowData.parent_list[currentLevel]
    let orderIndex = rowData.parent_list[currentLevel + 1]?.order_index || 0
    if (!target) return
    if (target.id === dragItem?.id) {
      const tmp = rowData.parent_list[rowData.parent_list.length - 2]
      target = tmp
      orderIndex = rowData.parent_list[rowData.parent_list.length - 1]?.order_index || 0
    }
    setMoveTarget({ id: dragItem?.id || 0, parent_id: target.id, name: target.name, level: currentLevel, order_index: orderIndex })
  }

  const toggleCreateMode = () => {
    if (isCreateMode) {
      setIsCreateMode(false)
      dispatch(updateSettingTeamCreateTeam(false))
    } else {
      setIsCreateMode(true)
      dispatch(updateSettingTeamCreateTeam(true))
    }
  }

  const toggleOpenTeam = () => {
    if (openTeamIds.length === openableTeamIds.length) {
      setOpenTeamIds([])
      dispatch(updateSettingTeamOpenTeamIds([]))
    } else {
      setOpenTeamIds(openableTeamIds)
      dispatch(updateSettingTeamOpenTeamIds(openableTeamIds))
    }
  }

  const changeManagerTeamPermission = () => {
    const queueId = enqueue({
      type: "progress",
      message: "設定を更新しています…",
    })
    const tmpParam: RequestPatchSettingTeamManager = {
      id: managerDetail?.member_id.toString() || "0",
      team_id: selectTeam.id.toString(),
      setting_type: selectTabType === "current" ? "self" : "nested",
      use_team_setting: selectManagerDetailCommonType,
    }
    if (!selectManagerDetailCommonType) {
      tmpParam.permission = selectManagerDetailTeamPermission
    }
    dispatch(
      updateSettingTeamManager(
        tmpParam,
        () => {
          displayUpdateToast(queueId)
          setManagerDetail(undefined)
          dispatch(
            fetchSettingTeamDetail(
              { id: selectTeam.id.toString() },
              (resultDetail) => {
                setSelectTeam({ ...resultDetail, isCommon: !teamList.find((v) => v.id === selectTeam.id)?.parent_team_id })
                setSelectManager(resultDetail.managers)
                setSelectMember(resultDetail.self.members)
                setInputName(resultDetail.name)
                setInputCode(resultDetail.code)
                setIsDetailLoading(false)
              },
            ),
          )
        },
      ),
    )
  }

  const snapLeftToCursor: Modifier = ({ transform, activeNodeRect, activatorEvent }) => {
    if (!activeNodeRect || !activatorEvent) {
      return transform
    }

    const event = 'touches' in activatorEvent
      ? (activatorEvent as TouchEvent).touches[0]
      : (activatorEvent as MouseEvent)

    if (!event || event.clientX === undefined) {
      return transform
    }

    const currentMouseX = event.clientX + transform.x
    const elementInitialX = activeNodeRect.left

    return {
      ...transform,
      x: currentMouseX - elementInitialX + 20,
    }
  }

  const openManagerDetail = (data: permissionDetail | undefined) => {
    if (!data) return
    setManagerDetail(data)
    setSelectManagerDetailCommonType(data.use_team_setting)
    if (data.use_team_setting) {
      setSelectManagerDetailTeamPermission(teamPermission)
    } else {
      setSelectManagerDetailTeamPermission(data.permission)
    }
  }

  useEffect(() => {
    setIsCreateMode(current.settingTeamCreateTeam)
  }, [current])

  useEffect(() => {
    dispatch(
      fetchSettingTeam((result) => {
        setIsLoading(false)
        setTeamList(result.teams)
        setMemberList(result.members)
        setUnassignedMembers(result.unassigned_members)
        dispatch(
          fetchSettingTeamDetail(
            { id: result.teams.find((v) => !v.parent_team_id)?.id.toString() || "0" },
            (resultDetail) => {
              setSelectTeamId(resultDetail.id)
              setIsDisplayDetail(true)
              setSelectTeam({ ...resultDetail, isCommon: !teamList.find((v) => v.id === selectTeam.id)?.parent_team_id })
              setSelectManager(resultDetail.managers)
              setSelectMember(resultDetail.self.members)
              setInputName(resultDetail.name)
              setInputCode(resultDetail.code)
              setIsDetailLoading(false)
            },
          ),
        )
      }),
    )
  }, [])

  useEffect(() => {
    const formatName = replaceAndStrip(inputName)
    const timer = setTimeout(() => {
      if (isUpdate && !nameComposition && formatName) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        setTeamList((cur) => cur.map((v) => (v.id === selectTeam?.id ? { ...v, name: formatName } : v)))
        dispatch(
          updateSettingTeam(
            {
              id: selectTeam?.id.toString() || "0",
              name: formatName,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [inputName, nameComposition])

  useEffect(() => {
    const formatCode = replaceAndStrip(inputCode || "")
    const timer = setTimeout(() => {
      if (isUpdate && !codeComposition && formatCode) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        setTeamList((cur) => cur.map((v) => (v.id === selectTeam?.id ? { ...v, code: formatCode } : v)))
        dispatch(
          updateSettingTeam(
            {
              id: selectTeam?.id.toString() || "0",
              code: formatCode,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [inputCode, codeComposition])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        const beforeIds = selectTeam.managers.map((v) => v.id)
        const afterIds = selectManager.map((v) => v.id)
        const addIds = afterIds.filter((v) => !beforeIds.includes(v))
        const delIds = beforeIds.filter((v) => !afterIds.includes(v))
        const tmpParam: RequestPatchSettingTeam = { id: selectTeam?.id.toString() || "0" }
        if (addIds.length) tmpParam.added_manager_ids = addIds
        if (delIds.length) tmpParam.removed_manager_ids = delIds
        dispatch(
          updateSettingTeam(
            tmpParam,
            () => {
              displayUpdateToast(queueId)
              setSelectTeam((cur) => ({
                ...cur,
                managers: [...cur.managers.filter((v) => !delIds.includes(v.id)), ...memberList.filter((v) => addIds.includes(v.id))],
              }))
              refreshUnassignedMembers()
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [selectManager])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        const beforeIds = selectTeam.self.members.map((v) => v.id)
        const afterIds = selectMember.map((v) => v.id)
        const addIds = afterIds.filter((v) => !beforeIds.includes(v))
        const delIds = beforeIds.filter((v) => !afterIds.includes(v))
        const tmpParam: RequestPatchSettingTeam = { id: selectTeam?.id.toString() || "0" }
        if (addIds.length) tmpParam.added_member_ids = addIds
        if (delIds.length) tmpParam.removed_member_ids = delIds
        dispatch(
          updateSettingTeam(
            tmpParam,
            () => {
              displayUpdateToast(queueId)
              refreshUnassignedMembers()
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [selectMember])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        const tmpParam: RequestPatchSettingTeam = { id: selectTeam?.id.toString() || "0" }
        if (selectTabType === "current") {
          setSelectTeam((cur) => ({ ...cur, self: { ...cur.self, permission: teamPermission } }))
          tmpParam.self_permission = teamPermission
        } else {
          setSelectTeam((cur) => ({ ...cur, nested: { ...cur.nested, permission: teamPermission } }))
          tmpParam.nested_permission = teamPermission
        }
        dispatch(
          updateSettingTeam(
            tmpParam,
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [teamPermission])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate && isSortSubmit) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        const tmpParam: RequestPatchSettingTeam = {
          id: moveTarget?.id.toString() || "0",
          parent_team_id: moveTarget?.parent_id,
          order_index: moveTarget?.order_index,
        }
        dispatch(
          updateSettingTeam(
            tmpParam,
            () => {
              displayUpdateToast(queueId)
              setDragItem(undefined)
              dispatch(
                fetchSettingTeam((result) => {
                  setIsLoading(false)
                  setTeamList(result.teams)
                  setMemberList(result.members)
                  setUnassignedMembers(result.unassigned_members)
                  setIsSortSubmit(false)
                }),
              )
            },
          ),
        )
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [isSortSubmit])

  useEffect(() => {
    setOpenTeamIds(current.settingTeamOpenTeamIds)
  }, [current])

  useEffect(() => {
    if (current.settingTeamOpenTeamIds.length) return
    setOpenTeamIds(openableTeamIds)
  }, [openableTeamIds])

  return (
    <section className="setting_page" style={{ cursor: isGripping ? "none" : "initial" }}>
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - チーム設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        チーム設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          チーム設定
          {/* <VideoGuide
            videoId="hA2cIraIfd8"
          /> */}
        </h1>
      </section>
      <section className="page_description">
        <p>
          新規チームの作成、既存チームの編集、削除をすることができます。
        </p>
        <p>
          マネージャーとして登録されたユーザーは、チームメンバーに対して、カスタムアラートの設定やリスクアセスメントの参照をすることができます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div
          className="content-block"
          style={{
            borderBottom: "none",
          }}
        >
          <div className="block-detail">
            {
              isLoading ? (
                <LoadingSquare>
                  <>
                    チーム情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "40px",
                    }}
                  >
                    <div
                      style={{
                        width: isDisplayDetail ? "calc(100% - 680px)" : "100%",
                        display: "flex",
                        flexFlow: "column",
                        gap: "16px",
                      }}
                    >
                      {
                        !!unassignedMembers.length && (
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              チームに所属していないユーザー
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "4px",
                                border: "dashed 2px #999999",
                                borderRadius: "4px",
                                padding: "8px",
                                maxHeight: "150px",
                                overflowY: "scroll",
                              }}
                            >
                              {
                                unassignedMembers.map((v) => (
                                  <div
                                    style={{
                                      position: "relative",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "left",
                                      userSelect: "none",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "20px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <img
                                        alt={v.name}
                                        src={v.profile_image ?? `/static/images/profile/${(v.id || 0) % 12}.jpeg`}
                                        style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                                      />
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "center",
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <ruby>
                                          {v.name}
                                          <rt>{v.kana}</rt>
                                        </ruby>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )
                      }
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "4px",
                            position: "sticky",
                            top: "52px",
                            backgroundColor: "#FFFFFF",
                            zIndex: "1",
                            padding: "4px 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              backgroundColor: "#FFFFFF",
                              padding: "6px 0",
                              position: "relative",
                            }}
                          >
                            {
                              !isSortMode && (
                                <button
                                  type="button"
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "-21px",
                                    transform: "translate(-50%, -50%)",
                                    display: "flex",
                                    flexFlow: "column",
                                    alignItems: "center",
                                    gap: "2px",
                                    padding: "0",
                                    color: "#666666",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                  onClick={toggleOpenTeam}
                                >
                                  <div
                                    style={{
                                      fontSize: "6px",
                                    }}
                                  >
                                    {`すべて${openTeamIds.length === openableTeamIds.length ? "閉じる" : "展開"}`}
                                  </div>
                                  <div>
                                    <FontAwesomeIcon icon={['fas', 'sitemap']} />
                                  </div>
                                </button>
                              )
                            }
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              チーム一覧
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                right: "0",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  border: "solid 1px #666666",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  cursor: "pointer",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px 0 0 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    backgroundColor: isSortMode ? "#EEEEEE" : "#FFFFFF",
                                    color: isSortMode ? "#999999" : "#333333",
                                    userSelect: "none",
                                  }}
                                  onClick={() => setIsSortMode(false)}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={["fas", "sitemap"]} />
                                  階層表示
                                </div>
                                <div
                                  style={{
                                    width: "1px",
                                    height: "21px",
                                    backgroundColor: "#999999",
                                  }}
                                />
                                <div
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "0 4px 4px 0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    backgroundColor: isSortMode ? "#FFFFFF" : "#EEEEEE",
                                    color: isSortMode ? "#333333" : "#999999",
                                    userSelect: "none",
                                  }}
                                  onClick={() => setIsSortMode(true)}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={["fas", "sort-amount-down"]} />
                                  並び替え
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "10px",
                                  }}
                                >
                                  チーム作成
                                </div>
                                <div
                                  style={{
                                    color: isCreateMode ? "#008800" : "#666666",
                                    fontSize: "24px",
                                    cursor: "pointer",
                                  }}
                                  onClick={toggleCreateMode}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={["fas", isCreateMode ? "toggle-on" : "toggle-off"]} />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="search_area">
                              <input
                                type="text"
                                placeholder="チーム名をクイック検索"
                                onChange={(event) => searchTeam(event.target.value)}
                                value={searchWord}
                                style={{ width: "100%" }}
                              />
                              {
                                searchWord && (
                                  <button type="button" className="reset" onClick={reset}>
                                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                                  </button>
                                )
                              }
                              {
                                searchWord && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: "0",
                                      border: "solid 1px #CCCCCC",
                                      width: "100%",
                                      height: "200px",
                                      overflowY: "scroll",
                                      backgroundColor: "#FFFFFF",
                                      zIndex: "1",
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      teams.length ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          {
                                            teams.map((v) => (
                                              <div
                                                className="hover"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  padding: "8px",
                                                  whiteSpace: "nowrap",
                                                  cursor: "pointer",
                                                  userSelect: "none",
                                                }}
                                                onClick={(e) => {
                                                  clickTeam(v.id)
                                                  reset()
                                                  e.stopPropagation()
                                                }}
                                                role="none"
                                              >
                                                {buildFullPathName(v.id)}
                                              </div>
                                            ))
                                          }
                                        </div>
                                      ) : (
                                        <>
                                          {`「${searchWord}」に該当するチーム名がありませんでした`}
                                        </>
                                      )
                                    }
                                  </div>
                                )
                              }
                            </div>
                          </div>
                        </div>
                        <div
                          className={`${!isSortMode && "border_list"}`}
                        >
                          {
                            isSortMode ? (
                              <DndContext
                                autoScroll
                                sensors={sensors}
                                measuring={{
                                  droppable: { strategy: MeasuringStrategy.BeforeDragging },
                                }}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragMove={handleDragMove}
                              >
                                <SortableContext
                                  items={flatTeams.map((v) => `project_category_row_${v.id}`)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {buildSortTeamList()}
                                </SortableContext>
                                <DragOverlay
                                  dropAnimation={null}
                                  modifiers={[snapLeftToCursor]}
                                >
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      backgroundColor: "#FFFFFF",
                                      width: "300px",
                                      height: "40px",
                                      padding: "8px",
                                      display: "flex",
                                      flexFlow: "column",
                                      justifyContent: "center",
                                      cursor: "none !important",
                                      margin: 0,
                                      transformOrigin: '0 0',
                                      pointerEvents: 'none',
                                      position: "relative",
                                    }}
                                  >
                                    <div>
                                      {dragItem?.name}
                                    </div>
                                  </div>
                                </DragOverlay>
                              </DndContext>
                            ) : (
                              <>
                                {buildTeamList({ level: 0, isCreate: true })}
                              </>
                            )
                          }
                        </div>
                      </div>
                    </div>
                    {
                      isDisplayDetail && (
                        <div
                          style={{
                            width: "640px",
                            position: "relative",
                          }}
                        >
                          <div
                            key={`selected_team_${selectTeam?.id}`}
                            style={{
                              width: "100%",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "16px",
                              position: "sticky",
                              top: "60px",
                              overflowY: "scroll",
                              scrollbarWidth: "none",
                              maxHeight: "100vh",
                            }}
                          >
                            {
                              isDetailLoading ? (
                                <>
                                  <LoadingSquare>
                                    <>
                                      チーム情報を取得しています…
                                    </>
                                  </LoadingSquare>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "4px",
                                      left: "8px",
                                      fontSize: "20px",
                                      color: "#CCCCCC",
                                      padding: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={closeDetail}
                                      style={{
                                        padding: "0",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'angle-double-right']} />
                                    </button>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      {teamList.find((v) => !v.parent_team_id)?.name}
                                      {!!teamList.find((v) => v.id === selectTeam.id)?.parent_team_id && " > " }
                                      {buildFullPathName(selectTeam.id)}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      borderBottom: "solid 1px #CCCCCC",
                                      padding: "8px",
                                      marginTop: "16px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "end",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "end",
                                          gap: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "2px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "10px",
                                            }}
                                          >
                                            チーム名
                                          </div>
                                          {
                                            selectTeam?.isCommon ? (
                                              <div
                                                style={{
                                                  width: "200px",
                                                  height: "30px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  fontSize: "14px",
                                                }}
                                              >
                                                {selectTeam?.name}
                                              </div>
                                            ) : (
                                              <div
                                                style={{
                                                  position: "relative",
                                                }}
                                              >
                                                <input
                                                  type="text"
                                                  placeholder="チーム名を入力"
                                                  defaultValue={selectTeam?.name}
                                                  style={{
                                                    fontSize: "14px",
                                                    width: "200px",
                                                    height: "30px",
                                                  }}
                                                  value={inputName}
                                                  onChange={(e) => changeInputName(e.target.value)}
                                                  onCompositionStart={() => setNameComposition(true)}
                                                  onCompositionEnd={() => setNameComposition(false)}
                                                />
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "16px",
                                                    transform: "translateY(-50%)",
                                                    color: "#CCCCCC",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'pen']} />
                                                </div>
                                              </div>
                                            )
                                          }
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "2px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "10px",
                                            }}
                                          >
                                            部門コード
                                          </div>
                                          {
                                            selectTeam.isCommon ? (
                                              <div
                                                style={{
                                                  width: "200px",
                                                  height: "30px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  fontSize: "14px",
                                                }}
                                              >
                                                {selectTeam?.code || "部門コードなし"}
                                              </div>
                                            ) : (
                                              <div
                                                style={{
                                                  position: "relative",
                                                }}
                                              >
                                                <input
                                                  type="text"
                                                  placeholder="部門コードを入力"
                                                  value={inputCode || ""}
                                                  onChange={(e) => changeInputCode(e.target.value)}
                                                  onCompositionStart={() => setCodeComposition(true)}
                                                  onCompositionEnd={() => setCodeComposition(false)}
                                                  style={{
                                                    fontSize: "14px",
                                                    width: "200px",
                                                  }}
                                                />
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "16px",
                                                    transform: "translateY(-50%)",
                                                    color: "#CCCCCC",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'pen']} />
                                                </div>
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                      {
                                        !selectTeam.isCommon && (
                                          <div
                                            style={{
                                              position: "relative",
                                            }}
                                          >
                                            <button
                                              type="button"
                                              onClick={() => setIsDisplayMiniMenu(true)}
                                              style={{
                                                fontSize: "9px",
                                                display: "flex",
                                                flexFlow: "column",
                                                alignItems: "center",
                                                gap: "4px",
                                                padding: 0,
                                              }}
                                            >
                                              <FontAwesomeIcon icon={['fas', 'cog']} style={{ fontSize: "16px" }} />
                                              操作
                                            </button>
                                            {
                                              isDisplayMiniMenu && (
                                                <>
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      right: 0,
                                                      backgroundColor: "#FFFFFF",
                                                      width: "180px",
                                                      zIndex: "2005",
                                                      borderRadius: "4px",
                                                      display: "flex",
                                                      flexFlow: "column",
                                                      gap: "4px",
                                                      padding: "4px",
                                                    }}
                                                  >
                                                    <button
                                                      type="button"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        padding: "8px",
                                                        color: "#FF0000",
                                                      }}
                                                      onClick={() => setDeleteTeamData({ id: selectTeam.id, name: selectTeam.name })}
                                                    >
                                                      <FontAwesomeIcon icon={['fas', 'trash-alt']} fixedWidth />
                                                      削除する
                                                    </button>
                                                    <button
                                                      type="button"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        padding: "8px",
                                                      }}
                                                      onClick={() => setSelectParentTeamData({ id: selectTeam.id, name: selectTeam.name })}
                                                    >
                                                      <FontAwesomeIcon icon={['fas', 'sitemap']} fixedWidth />
                                                      階層を変更する
                                                    </button>
                                                  </div>
                                                  <div
                                                    className="close_screen gray"
                                                    role="presentation"
                                                    onClick={() => setIsDisplayMiniMenu(false)}
                                                  />
                                                </>
                                              )
                                            }
                                          </div>
                                        )
                                      }
                                    </div>
                                    {
                                      !!nameError && (
                                        <div
                                          style={{
                                            padding: "8px 0",
                                            color: "#FF0000",
                                            fontSize: "10px",
                                          }}
                                        >
                                          {nameError}
                                        </div>
                                      )
                                    }
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "baseline",
                                      padding: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: "4px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      マネージャー
                                      <span
                                        style={{
                                          fontSize: "26px",
                                        }}
                                      >
                                        {selectManager.length || 0}
                                      </span>
                                      名
                                    </div>
                                    <div>
                                      <AddMemberModule
                                        memberIdList={selectManager.map((v) => v.id)}
                                        callbackMembers={(val) => changeManager(val.map((v) => v.id))}
                                        memberList={
                                          memberList.map((v) => ({
                                            id: v.id, name: v.name, profile_image: v.profile_image, active: true,
                                          }))
                                        }
                                        title="マネージャー編集"
                                      />
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      flexWrap: "wrap",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      overflowY: "scroll",
                                      scrollbarWidth: "none",
                                      maxHeight: "200px",
                                    }}
                                  >
                                    {selectManager.map((v) => memberChip(v, ejectManagerId))}
                                    {
                                      !selectManager.length && (
                                        <div
                                          style={{
                                            margin: "0 8px",
                                          }}
                                        >
                                          マネージャーが設定されていません
                                        </div>
                                      )
                                    }
                                  </div>
                                  <div
                                    style={{
                                      marginTop: "24px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "4px",
                                        marginBottom: "-0.5px",
                                        padding: "0 8px",
                                      }}
                                    >
                                      {
                                        (selectTeam.nested.teams.length ? DETAIL_TAB_TYPE : DETAIL_TAB_TYPE.filter((v) => v.type === "current"))
                                          .map((v) => (
                                            <div
                                              style={{
                                                border: `solid 1px ${selectTabType === v.type ? "#999999" : "#CCCCCC"}`,
                                                borderBottom: selectTabType === v.type ? "solid 2px #FFFFFF" : "none",
                                                borderRadius: "4px 4px 0 0",
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                              }}
                                              onClick={() => setSelectTabType(v.type)}
                                              role="none"
                                            >
                                              <FontAwesomeIcon icon={["fas", v.icon as IconName]} />
                                              {v.title}
                                            </div>
                                          ))
                                      }
                                    </div>
                                    <div
                                      style={{
                                        border: "solid 1px #666666",
                                        borderRadius: "4px",
                                        padding: "16px",
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "16px",
                                        marginTop: "-1px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "8px",
                                        }}
                                      >
                                        {
                                          selectTabType === "current" && (
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "baseline",
                                                padding: "8px",
                                                height: "50px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "baseline",
                                                  gap: "4px",
                                                  fontSize: "14px",
                                                }}
                                              >
                                                対象メンバー
                                                <span
                                                  style={{
                                                    fontSize: "26px",
                                                  }}
                                                >
                                                  {selectMember.length || 0}
                                                </span>
                                                名
                                              </div>
                                              <div>
                                                <AddMemberModule
                                                  memberIdList={selectMember.map((v) => v.id)}
                                                  callbackMembers={(val) => changeMember(val.map((w) => w.id))}
                                                  memberList={
                                                    memberList.map((v) => ({
                                                      id: v.id, name: v.name, profile_image: v.profile_image, active: true,
                                                    }))
                                                  }
                                                  title="メンバー編集"
                                                />
                                              </div>
                                            </div>
                                          )
                                        }
                                        {
                                          selectTabType === "other" && (
                                            <div
                                              style={{
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              配下チームの対象マネージャー・メンバー
                                            </div>
                                          )
                                        }
                                        {
                                          selectTabType === "current" && (
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "4px",
                                                flexWrap: "wrap",
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                padding: "8px",
                                                overflowY: "scroll",
                                                scrollbarWidth: "none",
                                                maxHeight: "200px",
                                              }}
                                            >
                                              {selectMember.map((v) => memberChip(v, ejectMemberId))}
                                              {
                                                !selectMember.length && (
                                                  <div
                                                    style={{
                                                      margin: "0 8px",
                                                    }}
                                                  >
                                                    メンバーが設定されていません
                                                  </div>
                                                )
                                              }
                                            </div>
                                          )
                                        }
                                        {
                                          selectTabType === "other" && (
                                            <div
                                              style={{
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "16px",
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                padding: "16px 8px 8px",
                                                overflowY: "scroll",
                                                scrollbarWidth: "none",
                                                maxHeight: "400px",
                                              }}
                                            >
                                              {
                                                selectTeam.nested.teams.map((v) => (
                                                  <div
                                                    style={{
                                                      position: "relative",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: "8px",
                                                        transform: "translateY(-50%)",
                                                        backgroundColor: "#FFFFFF",
                                                        padding: "0 8px",
                                                      }}
                                                    >
                                                      チーム名：
                                                      <span
                                                        style={{
                                                          fontWeight: "bold",
                                                        }}
                                                      >
                                                        {v.name}
                                                      </span>
                                                    </div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        gap: "4px",
                                                        flexWrap: "wrap",
                                                        border: "solid 1px #CCCCCC",
                                                        borderRadius: "4px",
                                                        padding: "16px 8px 8px",
                                                      }}
                                                    >
                                                      {
                                                        Array.from(new Map([...v.managers, ...v.members].map((w) => [w.id, w])).values())
                                                          .map((w) => memberChip(w))
                                                      }
                                                      {
                                                        (!v.managers.length && !v.members.length) && (
                                                          <>
                                                            メンバー・マネージャーはいません
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                              {
                                                !selectTeam.nested.teams.length && (
                                                  <div
                                                    style={{
                                                      margin: "0 8px 8px",
                                                    }}
                                                  >
                                                    配下チームはありません
                                                  </div>
                                                )
                                              }
                                            </div>
                                          )
                                        }
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "8px",
                                          padding: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          チームマネージャーの基本設定
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            backgroundColor: "#F1F6FE",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                              padding: "16px",
                                            }}
                                          >
                                            <div>
                                              閲覧できるオプションレポートの種類
                                            </div>
                                            {
                                              VIEW_OTHER_PERMISSION_LIST.map((v) => (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "4px 8px",
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() => changePermission(v.type)}
                                                  role="none"
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                                                    {v.label}
                                                    <span>{v.label_sub}</span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "20px",
                                                      color: teamPermission[v.type as TeamPermissionKeys] ? "#008800" : "#999999",
                                                    }}
                                                  >
                                                    <FontAwesomeIcon
                                                      icon={[
                                                        'fas',
                                                        teamPermission[v.type as TeamPermissionKeys] ? 'toggle-on' : 'toggle-off',
                                                      ]}
                                                    />
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                              padding: "16px",
                                            }}
                                          >
                                            <div>
                                              閲覧できる工数管理レポートの種類
                                            </div>
                                            {
                                              VIEW_TASK_PERMISSION_LIST.map((v) => (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "4px 8px",
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() => changePermission(v.type)}
                                                  role="none"
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                                                    {v.label}
                                                    <span>{v.label_sub}</span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "20px",
                                                      color: teamPermission[v.type as TeamPermissionKeys] ? "#008800" : "#999999",
                                                    }}
                                                  >
                                                    <FontAwesomeIcon
                                                      icon={[
                                                        'fas',
                                                        teamPermission[v.type as TeamPermissionKeys] ? 'toggle-on' : 'toggle-off',
                                                      ]}
                                                    />
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "8px",
                                          padding: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          チームマネージャーの個別設定
                                        </div>
                                        <div
                                          style={{
                                            overflowY: "scroll",
                                            scrollbarWidth: "none",
                                            maxHeight: "300px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              position: "relative",
                                              gap: "1px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: "200px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  borderBottom: "solid 1px #CCCCCC",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: "200px",
                                                    padding: "4px",
                                                    textAlign: "center",
                                                    fontWeight: "bold",
                                                    minHeight: "40px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    userSelect: "none",
                                                    borderRight: "solid 1px #CCCCCC",
                                                  }}
                                                >
                                                  名前
                                                </div>
                                              </div>
                                              {
                                                selectTeam?.managers.map((v) => (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      borderBottom: "solid 1px #CCCCCC",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        position: "relative",
                                                        width: "200px",
                                                        padding: "4px 8px",
                                                        textAlign: "center",
                                                        minHeight: "40px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        userSelect: "none",
                                                        borderRight: "solid 1px #CCCCCC",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          gap: "8px",
                                                        }}
                                                      >
                                                        <img
                                                          alt={v.name}
                                                          src={v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                                          style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                                                        />
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            flexFlow: "column",
                                                            justifyContent: "center",
                                                            alignItems: "flex-start",
                                                          }}
                                                        >
                                                          <ruby>
                                                            {v.name}
                                                            <rt>{v.kana}</rt>
                                                          </ruby>
                                                        </div>
                                                      </div>
                                                      <button
                                                        type="button"
                                                        // eslint-disable-next-line max-len
                                                        onClick={() => openManagerDetail(permissionData.individual_settings.find((w) => w.member_id === v.id))}
                                                        style={{
                                                          backgroundColor: "#FFFFFF",
                                                          border: "solid 1px #CCCCCC",
                                                          borderRadius: "4px",
                                                          padding: "2px 4px",
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        選択
                                                      </button>
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                              {
                                                !selectTeam?.managers.length && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      borderBottom: "solid 1px #CCCCCC",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        position: "relative",
                                                        width: "200px",
                                                        padding: "4px 8px",
                                                        textAlign: "center",
                                                        minHeight: "40px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        userSelect: "none",
                                                        borderRight: "solid 1px #CCCCCC",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          gap: "8px",
                                                        }}
                                                      >
                                                        -
                                                      </div>
                                                    </div>
                                                  </div>
                                                )
                                              }
                                            </div>
                                            <div
                                              style={{
                                                width: `calc(100% - 200px)`,
                                                overflowX: "scroll",
                                                scrollbarWidth: "none",
                                                position: "relative",
                                                marginLeft: "-1px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  position: "relative",
                                                  width: "fit-content",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    borderBottom: "solid 1px #CCCCCC",
                                                  }}
                                                >
                                                  {
                                                    VIEW_PERMISSION_LIST.map((v, i) => (
                                                      <div
                                                        style={{
                                                          width: v.size,
                                                          padding: "4px",
                                                          fontSize: "8px",
                                                          textAlign: "center",
                                                          minHeight: "40px",
                                                          display: "flex",
                                                          gap: "2px",
                                                          flexFlow: "column",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          userSelect: "none",
                                                          borderRight: i === VIEW_PERMISSION_LIST.length - 1 ? "none" : "solid 1px #CCCCCC",
                                                        }}
                                                      >
                                                        <FontAwesomeIcon icon={['fas', v.icon as IconName]} style={{ fontSize: "18px" }} />
                                                        {v.label_short}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                                {
                                                  selectTeam?.managers.map((v) => (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        borderBottom: "solid 1px #CCCCCC",
                                                        position: "relative",
                                                      }}
                                                    >
                                                      {
                                                        VIEW_PERMISSION_LIST.map((w, i) => (
                                                          <div
                                                            style={{
                                                              width: w.size,
                                                              padding: "4px",
                                                              textAlign: "center",
                                                              minHeight: "40px",
                                                              display: "flex",
                                                              gap: "2px",
                                                              flexFlow: "column",
                                                              alignItems: "center",
                                                              justifyContent: "center",
                                                              userSelect: "none",
                                                              borderRight: i === VIEW_PERMISSION_LIST.length - 1 ? "none" : "solid 1px #CCCCCC",
                                                              // eslint-disable-next-line max-len
                                                              color: permissionData.individual_settings.find((x) => x.member_id === v.id)?.permission[w.type as TeamPermissionKeys]
                                                                ? "#008800"
                                                                : "#FF0000",
                                                              fontWeight: "bold",
                                                            }}
                                                          >
                                                            {
                                                              // eslint-disable-next-line max-len
                                                              !permissionData.individual_settings.find((x) => x.member_id === v.id)?.use_team_setting && (
                                                                <>
                                                                  {
                                                                    // eslint-disable-next-line max-len
                                                                    permissionData.individual_settings.find((x) => x.member_id === v.id)?.permission[w.type as TeamPermissionKeys] ? "ON" : "OFF"
                                                                  }
                                                                </>
                                                              )
                                                            }
                                                          </div>
                                                        ))
                                                      }
                                                      {
                                                        permissionData.individual_settings.find((x) => x.member_id === v.id)?.use_team_setting && (
                                                          <div
                                                            style={{
                                                              position: "absolute",
                                                              top: 0,
                                                              left: 0,
                                                              height: "100%",
                                                              width: "100%",
                                                              backgroundColor: "#EEEEEE",
                                                              display: "flex",
                                                              alignItems: "center",
                                                              justifyContent: "center",
                                                              fontSize: "14px",
                                                              color: "#666666",
                                                            }}
                                                          >
                                                            マネージャー基本設定に準拠する
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  ))
                                                }
                                                {
                                                  !selectTeam?.managers.length && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        borderBottom: "solid 1px #CCCCCC",
                                                        position: "relative",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          height: "100%",
                                                          width: "100%",
                                                          backgroundColor: "#FFFFFF",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          fontSize: "14px",
                                                          color: "#666666",
                                                          minHeight: "40px",
                                                          userSelect: "none",
                                                        }}
                                                      >
                                                        -
                                                      </div>
                                                    </div>
                                                  )
                                                }
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )
                            }
                          </div>
                        </div>
                      )
                    }
                  </div>
                </>
              )
            }
          </div>
        </div>
        <Modal
          show={!!managerDetail}
          wrapperClass="manager_detail"
          closeCallback={() => setManagerDetail(undefined)}
          closeable
        >
          <>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "8px",
              }}
            >
              <div>
                {selectTeam.name}
                のマネージャー個別設定
                {memberList.find((v) => v.id === managerDetail?.member_id)?.name}
              </div>
              <div
                style={{
                  padding: "8px",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: selectManagerDetailCommonType ? "#008800" : "#666666",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectManagerDetailCommonType(true)}
                  role="none"
                >
                  <FontAwesomeIcon icon={['fas', selectManagerDetailCommonType ? 'check-circle' : 'circle']} />
                  チームマネージャーの基本設定に準拠する
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: !selectManagerDetailCommonType ? "#008800" : "#666666",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectManagerDetailCommonType(false)}
                  role="none"
                >
                  <FontAwesomeIcon icon={['fas', !selectManagerDetailCommonType ? 'check-circle' : 'circle']} />
                  個別に設定する
                </div>
              </div>
              {
                !selectManagerDetailCommonType && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        backgroundColor: "#F1F6FE",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                          padding: "16px",
                        }}
                      >
                        <div>
                          閲覧できるオプションレポートの種類
                        </div>
                        {
                          VIEW_OTHER_PERMISSION_LIST.map((v) => (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#FFFFFF",
                                padding: "4px 8px",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleDetailPermission(v.type)}
                              role="none"
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                                {v.label}
                                <span>{v.label_sub}</span>
                              </div>
                              <div
                                style={{
                                  fontSize: "20px",
                                  color: selectManagerDetailTeamPermission[v.type as TeamPermissionKeys] ? "#008800" : "#999999",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', selectManagerDetailTeamPermission[v.type as TeamPermissionKeys] ? 'toggle-on' : 'toggle-off']}
                                />
                              </div>
                            </div>
                          ))
                        }
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                          padding: "16px",
                        }}
                      >
                        <div>
                          閲覧できる工数管理レポートの種類
                        </div>
                        {
                          VIEW_TASK_PERMISSION_LIST.map((v) => (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#FFFFFF",
                                padding: "4px 8px",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleDetailPermission(v.type)}
                              role="none"
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                                {v.label}
                                <span>{v.label_sub}</span>
                              </div>
                              <div
                                style={{
                                  fontSize: "20px",
                                  color: selectManagerDetailTeamPermission[v.type as TeamPermissionKeys] ? "#008800" : "#999999",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', selectManagerDetailTeamPermission[v.type as TeamPermissionKeys] ? 'toggle-on' : 'toggle-off']}
                                />
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                )
              }
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "8px",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  className="button cancel"
                  onClick={() => setManagerDetail(undefined)}
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #656E73",
                    color: "#656E73",
                    width: "200px",
                    textAlign: "center",
                    borderRadius: "4px",
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={changeManagerTeamPermission}
                  style={{
                    backgroundColor: "#1630c6",
                    border: "1px solid #1630c6",
                    color: "#FFFFFF",
                    width: "200px",
                    textAlign: "center",
                    borderRadius: "4px",
                  }}
                >
                  編集する
                </button>
              </div>
            </div>
          </>
        </Modal>
        <Modal
          show={!!createTeamData}
          wrapperClass="manager_detail"
          closeCallback={() => {
            setCreateTeamData(undefined)
            setInputNewName("")
            setNewNameError("")
          }}
          closeable
        >
          <>
            <div
              style={{
                fontSize: "18px",
                marginBottom: "24px",
              }}
            >
              新しいチームを作成します
            </div>
            <div>
              <input
                type="text"
                placeholder="チーム名を入力"
                defaultValue={createTeamData?.name}
                style={{
                  fontSize: "14px",
                  width: "100%",
                  height: "30px",
                }}
                value={inputNewName}
                onChange={(e) => changeInputNewName(e.target.value)}
              />
            </div>
            {
              !!newNameError && (
                <div
                  style={{
                    padding: "8px 0",
                    color: "#FF0000",
                    fontSize: "10px",
                  }}
                >
                  {newNameError}
                </div>
              )
            }
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "8px",
                gap: "8px",
              }}
            >
              <button
                type="button"
                className="button cancel"
                onClick={() => {
                  setCreateTeamData(undefined)
                  setInputNewName("")
                  setNewNameError("")
                }}
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #656E73",
                  color: "#656E73",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="button"
                onClick={createTeam}
                style={{
                  backgroundColor: newNameError ? "#b8bee6" : "#1630c6",
                  border: `1px solid ${newNameError ? "#b8bee6" : "#1630c6"}`,
                  color: "#FFFFFF",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                  cursor: newNameError ? "not-allowed" : "initial",
                }}
                disabled={!!newNameError}
              >
                作成する
              </button>
            </div>
          </>
        </Modal>
        <Modal
          show={!!deleteTeamData}
          wrapperClass="manager_detail"
          closeCallback={() => setDeleteTeamData(undefined)}
          closeable
        >
          <>
            <div
              style={{
                fontSize: "18px",
                marginBottom: "24px",
              }}
            >
              チームを削除します
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "4px",
                padding: "0 16px",
              }}
            >
              <div>
                {`${deleteTeamData?.name}を削除します。よろしいですか？`}
              </div>
              <div
                style={{
                  color: "#c61616",
                }}
              >
                チームを削除すると復元することはできません。
              </div>
              <div
                style={{
                  color: "#c61616",
                }}
              >
                また、下階層に属するチームも削除されます。
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <button
                type="button"
                className="button cancel"
                onClick={() => setDeleteTeamData(undefined)}
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #656E73",
                  color: "#656E73",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="button"
                onClick={deleteTeam}
                style={{
                  backgroundColor: "#c61616",
                  border: "1px solid #c61616",
                  color: "#FFFFFF",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                削除する
              </button>
            </div>
          </>
        </Modal>
        <Modal
          show={!!selectParentTeamData}
          wrapperClass="manager_detail"
          closeCallback={() => setSelectParentTeamData(undefined)}
          closeable
        >
          <>
            <div
              style={{
                fontSize: "18px",
                marginBottom: "24px",
              }}
            >
              チームを階層を変更します
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "4px",
                padding: "0 16px",
              }}
            >
              <div>
                {`チーム「${selectParentTeamData?.name}」の階層を変更します。`}
              </div>
              <div>
                {`チーム「${selectParentTeamData?.name}」の上階層にしたいチームを選択してください。`}
              </div>
              <div
                style={{
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  padding: "8px",
                  position: "relative",
                }}
              >
                <select
                  style={{ width: "100%" }}
                  onChange={(e) => setSelectParentTeamData((cur) => (cur ? ({ ...cur, parent_id: Number(e.target.value || 0) }) : undefined))}
                >
                  {
                    flatTeams.filter((v) => !v.parent_list?.some((w) => w.id === selectParentTeamData?.id)).map((v) => (
                      <option value={v.id}>
                        {v.name}
                      </option>
                    ))
                  }
                </select>
                <FontAwesomeIcon
                  icon={['fas', 'caret-down']}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <button
                type="button"
                className="button cancel"
                onClick={() => setSelectParentTeamData(undefined)}
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #656E73",
                  color: "#656E73",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="button"
                onClick={selectParentTeam}
                style={{
                  backgroundColor: "#1630c6",
                  border: "1px solid #1630c6",
                  color: "#FFFFFF",
                  width: "200px",
                  textAlign: "center",
                  borderRadius: "4px",
                }}
              >
                編集する
              </button>
            </div>
          </>
        </Modal>
      </section>
    </section>
  )
}

export default Index
